import Auth from "./Auth";
import Garage from "./Garage";
import ProfilePage from "./ProfilePage";
import Discover from "./Discover";
import CarPage from "./CarPage";
import SavedBuilds from "./SavedBuilds";
import ShowCard from "./ShowCard";
import ShowTools from "./ShowTools";
import ShowDisplay from "./ShowDisplay";
import BuildTimeline from "./BuildTimeline";
import About from "./About";
import Contact from "./Contact";
import ForSale from "./ForSale";
import Home from "./Home";
import Spotlight from "./Spotlight";
import BuildInvestment from "./BuildInvestment";
import BuildBinderPage from "./BuildBinderPage";
import { auth, db } from "./firebase";
import { collection, getDocs } from "firebase/firestore";
import { useState, useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import Account from "./Account";
import BottomNav from "./components/BottomNav";
import ScrollToTop from "./components/ScrollToTop";
import PageTransition from "./components/PageTransition";
import { AnimatePresence } from "framer-motion";
import Notifications from "./Notifications";
import PullToRefresh from "./components/PullToRefresh";
import MerchEngine from "./MerchEngine";
import MerchCollection from "./MerchCollection";
import MerchProduct from "./MerchProduct";
import MerchArtworkPreview from "./pages/MerchArtworkPreview";
import Pro from "./Pro";
import VehiclePlaque from "./pages/VehiclePlaque";
import VehicleArtwork from "./pages/admin/VehicleArtwork";

import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate
} from "react-router-dom";
import { isMerchAdminUser } from "./utils/merchAdmin";

function App() {
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [username, setUsername] = useState("");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (currentUser) => {
        if (!currentUser) {
          setIsLoggedIn(false);
          setHasProfile(false);
          setUsername("");
          setCheckingAuth(false);
          return;
        }

        setIsLoggedIn(true);

        const snapshot = await getDocs(collection(db, "profiles"));

        let foundProfile = false;
        let foundUsername = "";

        snapshot.forEach((docItem) => {
          const data = docItem.data();

          if (
            data.userId === currentUser.uid ||
            data.uid === currentUser.uid ||
            data.email === currentUser.email
          ) {
            foundProfile = true;
            foundUsername = data.username || docItem.id;
          }
        });

        setHasProfile(foundProfile);
        setUsername(foundUsername);
        setCheckingAuth(false);
      },
      (error) => {
        console.error("Authentication error:", error);
        setIsLoggedIn(false);
        setHasProfile(false);
        setUsername("");
        setCheckingAuth(false);
      }
    );

    const timeout = setTimeout(() => {
      setCheckingAuth(false);
    }, 2000);

    return () => {
      clearTimeout(timeout);
      unsubscribe();
    };
  }, []);

  if (checkingAuth) {
    return (
      <div style={loadingStyle}>
        Loading MyBuildID...
      </div>
    );
  }

  return (
    <BrowserRouter>
      <ScrollToTop />

      <PullToRefresh>
        <AnimatedRoutes
          isLoggedIn={isLoggedIn}
          hasProfile={hasProfile}
          username={username}
        />
      </PullToRefresh>

      <AppBottomNav
        isLoggedIn={isLoggedIn}
        hasProfile={hasProfile}
        username={username}
      />
    </BrowserRouter>
  );
}

function AppBottomNav({ isLoggedIn, hasProfile, username }) {
  const location = useLocation();

  const hideBottomNav =
    location.pathname.includes("/show-display") ||
    location.pathname.includes("/showcard") ||
    location.pathname.includes("/show-tools");

  if (!isLoggedIn || !hasProfile || hideBottomNav) {
    return null;
  }

  return <BottomNav username={username} />;
}

function AnimatedRoutes({ isLoggedIn, hasProfile, username }) {
  const location = useLocation();
  const isMerchAdmin = isMerchAdminUser({
    user: auth.currentUser,
    profile: { username }
  });

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/admin/vehicle-artwork" element={<PageTransition><VehicleArtwork /></PageTransition>} />
        <Route path="/user/:username/car/:carId/identity-studio/vehicle-plaque" element={<PageTransition><VehiclePlaque /></PageTransition>} />
        <Route
          path="/"
          element={
            <PageTransition>
              <Home />
            </PageTransition>
          }
        />

        <Route
          path="/auth"
          element={
            <PageTransition>
              <Auth />
            </PageTransition>
          }
        />

        <Route
          path="/discover"
          element={
            <PageTransition>
              <Discover />
            </PageTransition>
          }
        />

        <Route
          path="/spotlight"
          element={
            <PageTransition>
              <Spotlight />
            </PageTransition>
          }
        />

        <Route
          path="/for-sale"
          element={
            <PageTransition>
              <ForSale />
            </PageTransition>
          }
        />

        <Route
          path="/about"
          element={
            <PageTransition>
              <About />
            </PageTransition>
          }
        />

        <Route
          path="/contact"
          element={
            <PageTransition>
              <Contact />
            </PageTransition>
          }
        />

        <Route
          path="/setup"
          element={
            <PageTransition>
              {isLoggedIn ? (hasProfile ? <Garage /> : <Account />) : <Auth />}
            </PageTransition>
          }
        />

        <Route
          path="/garage"
          element={
            <PageTransition>
              {isLoggedIn ? (hasProfile ? <Garage /> : <Account />) : <Auth />}
            </PageTransition>
          }
        />

        <Route
          path="/account"
          element={
            <PageTransition>
              {isLoggedIn ? <Account /> : <Auth />}
            </PageTransition>
          }
        />

        <Route
          path="/pro"
          element={
            <PageTransition>
              <Pro />
            </PageTransition>
          }
        />

        <Route
          path="/saved"
          element={
            <PageTransition>
              {isLoggedIn ? (hasProfile ? <SavedBuilds /> : <Account />) : <Auth />}
            </PageTransition>
          }
        />

        <Route
          path="/merch"
          element={
            <PageTransition>
              {isLoggedIn ? (hasProfile ? <MerchEngine /> : <Account />) : <Auth />}
            </PageTransition>
          }
        />

        <Route
          path="/merch/collection/identity"
          element={
            <PageTransition>
              {isLoggedIn ? (hasProfile ? <MerchCollection /> : <Account />) : <Auth />}
            </PageTransition>
          }
        />

        <Route
          path="/merch/product/:productId"
          element={
            <PageTransition>
              {isLoggedIn ? (hasProfile ? <MerchProduct /> : <Account />) : <Auth />}
            </PageTransition>
          }
        />

        <Route
          path="/merch/artwork-preview"
          element={
            <PageTransition>
              {isLoggedIn ? (
                hasProfile ? (
                  isMerchAdmin ? <MerchArtworkPreview /> : <Navigate to="/merch" replace />
                ) : <Account />
              ) : <Auth />}
            </PageTransition>
          }
        />

        <Route
          path="/user/:username"
          element={
            <PageTransition>
              <ProfilePage />
            </PageTransition>
          }
        />

        <Route
          path="/user/:username/car/:carId"
          element={
            <PageTransition>
              <CarPage />
            </PageTransition>
          }
        />

        <Route
          path="/user/:username/car/:carId/binder"
          element={
            <PageTransition>
              <BuildBinderPage />
            </PageTransition>
          }
        />

        <Route
          path="/user/:username/car/:carId/show-tools"
          element={
            <PageTransition>
              <ShowTools />
            </PageTransition>
          }
        />

        <Route
          path="/user/:username/car/:carId/showcard"
          element={
            <PageTransition>
              <ShowCard />
            </PageTransition>
          }
        />

        <Route
          path="/user/:username/car/:carId/show-display"
          element={
            <PageTransition>
              <ShowDisplay />
            </PageTransition>
          }
        />

        <Route
          path="/notifications"
          element={
            <PageTransition>
              <Notifications />
            </PageTransition>
          }
        />

        <Route
          path="/user/:username/car/:carId/timeline"
          element={
            <PageTransition>
              <BuildTimeline />
            </PageTransition>
          }
        />

        <Route
          path="/user/:username/car/:carId/investment"
          element={
            <PageTransition>
              <BuildInvestment />
            </PageTransition>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

const loadingStyle = {
  background: "#111",
  color: "white",
  minHeight: "100vh",
  padding: "40px"
};

export default App;
